import dayjs, { type Dayjs } from 'dayjs';
import { jwtDecode } from 'jwt-decode';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useRbSystemAlert } from '@scmlatam/web-ui';
import { serviceSchedule } from '../services/service';
import type { Employee, Paycode } from '../types/services';
import type { Options } from '../types/hooks';
import { usePermisoSchedule } from '../../../../auth/permisosModulos';

type AmountMode = 'relative_amount' | 'numerical_amount';
type RelativeHours = 1 | 2;

interface UseFormUpdatePaycodeProps {
    employees: Employee[];
    paycodeAssignId: string | null;
    reload: () => Promise<void> | void;
    option: Options | null;
    setOption: (value: Options | null) => void;
}

interface JwtPayload {
    user_id: string;
}

interface PaycodeOption {
    value: string;
    label: string;
}

interface FormData {
    employeeId: string;
    paycode: string;
    date: Dayjs | null;
    start: Dayjs | null;
    amountDays: string;
    mode: AmountMode;
    amountHours: Dayjs | null;
    amountRelativeHours: RelativeHours;
    note: string;
    comment: string;
}

interface FieldError {
    error: boolean;
    message: string;
}

interface ValidationErrors {
    employeeId: FieldError;
    paycode: FieldError;
    date: FieldError;
    start: FieldError;
    amountDays: FieldError;
    amountHours: FieldError;
}

const initialValidationErrors = (): ValidationErrors => ({
    employeeId: { error: false, message: '' },
    paycode: { error: false, message: '' },
    date: { error: false, message: '' },
    start: { error: false, message: '' },
    amountDays: { error: false, message: '' },
    amountHours: { error: false, message: '' },
});

const emptyFormData = (): FormData => ({
    employeeId: '',
    paycode: '',
    date: null,
    start: null,
    amountDays: '1',
    mode: 'relative_amount',
    amountHours: null,
    amountRelativeHours: 1,
    note: '',
    comment: '',
});

const isSameFormData = (a: FormData, b: FormData): boolean =>
    a.employeeId === b.employeeId &&
    a.paycode === b.paycode &&
    (a.date?.valueOf() ?? null) === (b.date?.valueOf() ?? null) &&
    (a.start?.valueOf() ?? null) === (b.start?.valueOf() ?? null) &&
    a.amountDays === b.amountDays &&
    a.mode === b.mode &&
    (a.amountHours?.valueOf() ?? null) === (b.amountHours?.valueOf() ?? null) &&
    a.amountRelativeHours === b.amountRelativeHours &&
    a.note === b.note &&
    a.comment === b.comment;

const useFormUpdatePaycode = ({ employees, paycodeAssignId, reload, option, setOption }: UseFormUpdatePaycodeProps) => {
    const { t } = useTranslation('schedule.form.edit_paycode');
    const { showAlert } = useRbSystemAlert();
    const canEditPaycode = usePermisoSchedule('update_paycode');

    const [openForm, setOpenForm] = useState(false);
    const [searchEmployee, setSearchEmployee] = useState('');
    const [paycodes, setPaycodes] = useState<PaycodeOption[]>([]);
    const [searchPaycode, setSearchPaycode] = useState('');
    const [formData, setFormData] = useState<FormData>(emptyFormData());
    const [originalFormData, setOriginalFormData] = useState<FormData>(emptyFormData());
    const [loading, setLoading] = useState(false);
    const [loadingPaycodes, setLoadingPaycodes] = useState(false);
    const [loadingAssign, setLoadingAssign] = useState(false);
    const [validationMessage, setValidationMessage] = useState('');
    const [showMessage, setShowMessage] = useState(false);
    const [submitAttempted, setSubmitAttempted] = useState(false);

    useEffect(() => {
        if (option === 'edit_paycode_assign' && paycodeAssignId) {
            if (canEditPaycode) setOpenForm(true);
            setOption(null);
        }
    }, [option, paycodeAssignId, setOption, canEditPaycode]);

    const resetForm = () => {
        setSearchEmployee('');
        setSearchPaycode('');
        setFormData(emptyFormData());
        setOriginalFormData(emptyFormData());
        setValidationMessage('');
        setShowMessage(false);
        setSubmitAttempted(false);
    };

    const closeForm = () => {
        setOpenForm(false);
        resetForm();
    };

    const hasChanges = () => !isSameFormData(formData, originalFormData);

    const normalizedEmployees = employees.map((employee) => ({
        ...employee,
        value: String(employee.value),
    }));

    const fetchPaycodes = async () => {
        const { paycodes: list } = await serviceSchedule.getPaycodes({
            params: { t: 'standard', u: 'days' },
            alert: { t, showAlert },
        });

        setPaycodes(
            list
                .filter((paycode: Paycode) => paycode.unit === 'days')
                .map((paycode: Paycode) => ({
                    value: String(paycode.paycode_id),
                    label: paycode.name,
                })),
        );
    };

    const fetchAssign = async () => {
        if (!paycodeAssignId) return;

        const { paycodeAssign } = await serviceSchedule.getPaycodeAssignById({
            params: { paycode_assign_id: paycodeAssignId },
            alert: { t, showAlert },
        });

        if (!paycodeAssign) return;

        const parsedDate = paycodeAssign.date ? dayjs(paycodeAssign.date, 'YYYY-MM-DD') : null;
        const parsedStart = paycodeAssign.date && paycodeAssign.start ? dayjs(`${paycodeAssign.date} ${paycodeAssign.start}`, 'YYYY-MM-DD HH:mm') : null;
        const parsedAmountHours = paycodeAssign.date && paycodeAssign.amount ? dayjs(`${paycodeAssign.date} ${paycodeAssign.amount}`, 'YYYY-MM-DD HH:mm') : null;

        const next: FormData = {
            employeeId: paycodeAssign.id_employee ? String(paycodeAssign.id_employee) : '',
            paycode: paycodeAssign.id_paycode ? String(paycodeAssign.id_paycode) : '',
            date: parsedDate,
            start: parsedStart,
            amountDays: paycodeAssign.amount_day ? String(paycodeAssign.amount_day) : '1',
            mode: parsedAmountHours?.isValid() ? 'numerical_amount' : 'relative_amount',
            amountHours: parsedAmountHours?.isValid() ? parsedAmountHours : null,
            amountRelativeHours: 1,
            note: paycodeAssign.note ?? '',
            comment: paycodeAssign.comment ?? '',
        };

        setFormData(next);
        setOriginalFormData(next);
    };

    useEffect(() => {
        if (option === 'edit_paycode_assign' && paycodeAssignId) {
            if (canEditPaycode) setOpenForm(true);
            setOption(null);
        }
    }, [option, paycodeAssignId, setOption, canEditPaycode]);

    useEffect(() => {
        if (!openForm) return;

        setLoadingPaycodes(true);
        setLoadingAssign(true);
        Promise.all([fetchPaycodes().finally(() => setLoadingPaycodes(false)), fetchAssign().finally(() => setLoadingAssign(false))]);
    }, [openForm, paycodeAssignId]);

    const buildErrors = (): ValidationErrors => {
        const nextErrors = initialValidationErrors();

        if (!formData.employeeId) {
            nextErrors.employeeId = { error: true, message: t('edit_paycode.helper.worker') };
        }
        if (!formData.paycode) {
            nextErrors.paycode = { error: true, message: t('edit_paycode.helper.paycode') };
        }
        if (!formData.date?.isValid()) {
            nextErrors.date = { error: true, message: t('edit_paycode.helper.date') };
        }
        if (!formData.start?.isValid()) {
            nextErrors.start = { error: true, message: t('edit_paycode.helper.start_at') };
        }
        const amountDays = Number(formData.amountDays);
        if (!Number.isInteger(amountDays) || amountDays < 1) {
            nextErrors.amountDays = { error: true, message: t('edit_paycode.helper.amount_of_days') };
        }
        if (formData.mode === 'numerical_amount' && !formData.amountHours?.isValid()) {
            nextErrors.amountHours = { error: true, message: t('edit_paycode.helper.amount_of_hours') };
        }

        return nextErrors;
    };

    // Derived: errors are shown only after the first submit attempt and auto-clear as fields become valid.
    const validationErrors = useMemo<ValidationErrors>(() => (submitAttempted ? buildErrors() : initialValidationErrors()), [submitAttempted, formData, t]);

    const submit = async () => {
        if (!paycodeAssignId || !canEditPaycode) return;

        const accessToken = localStorage.getItem('access_token');

        if (!accessToken) {
            setValidationMessage(t('edit_paycode.alerts.validation.no_session'));
            return;
        }

        setSubmitAttempted(true);
        if (Object.values(buildErrors()).some((e) => e.error)) return;

        let userId: number;
        try {
            const decoded = jwtDecode<JwtPayload>(accessToken);
            userId = parseInt(decoded.user_id, 10);
            if (Number.isNaN(userId)) throw new Error('invalid user_id');
        } catch {
            setValidationMessage(t('edit_paycode.alerts.validation.no_session'));
            return;
        }

        setLoading(true);

        try {
            const ok = await serviceSchedule.updatePaycode({
                paycodeAssignId,
                body: {
                    amount: formData.amountHours?.format('HH:mm') ?? null,
                    amount_day: Number(formData.amountDays),
                    comment: formData.comment,
                    create_by: userId,
                    date: formData.date ? formData.date.format('YYYY-MM-DD') : '',
                    id_employee: parseInt(formData.employeeId, 10),
                    id_paycode: parseInt(formData.paycode, 10),
                    lastupdate_by: userId,
                    note: formData.note,
                    start: formData.start ? formData.start.format('HH:mm') : '',
                    template: 'Scheduler',
                    type: formData.mode === 'relative_amount' ? formData.amountRelativeHours : null,
                },
                alert: { t, showAlert },
            });

            if (ok) {
                showAlert('success', t('edit_paycode.alerts.save_success.message'));
                await reload();
                closeForm();
            }
        } finally {
            setLoading(false);
        }
    };

    const handleCancel = () => {
        if (hasChanges()) {
            setShowMessage(true);
        } else {
            closeForm();
        }
    };

    return {
        t,
        title: t('edit_paycode.title'),
        openForm,
        setOpenForm,
        closeForm,
        employees: normalizedEmployees,
        searchEmployee,
        setSearchEmployee,
        paycodes,
        searchPaycode,
        setSearchPaycode,
        loadingPaycodes,
        loadingAssign,
        formData,
        setFormData,
        loading,
        validationMessage,
        setValidationMessage,
        showMessage,
        setShowMessage,
        validationErrors,
        hasChanges,
        submit,
        handleCancel,
    };
};

export { useFormUpdatePaycode };
